/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Component voor de instructievideo (tot golf 4f een Custom Element): de knop in de tekenbalk, de
 * videopicker per modelleerniveau en de fullscreen video-overlay. Vervangt de AngularJS videobtn-/#videoholder-
 * markup in canvas.html en de video-scopefuncties in cytocanvas.controller.js. De play-state, de
 * logging, het startverzoek en het uitzetten van buitenaf lopen via de VideoStatusService.
 *
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 21-07-2026
 */
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    inject,
    OnDestroy,
    signal,
    untracked,
    ViewChild
} from '@angular/core';
import {TranslateModule, TranslatePipe, TranslateService} from '@ngx-translate/core';
import {BaseIslandComponent} from '../base-island.component';
import {DlModel} from '../../app/model/dl-model';
import {VideoStatusService} from '../../services/video-status.service';
import {CondtagService} from '../../services/condtag.service';

/**
 * Eén knop in de videopicker. `talen`/`kaal` vormen samen de statische lijst van beschikbare
 * videobestanden: `<pad>_<taal>.mp4` per taal in `talen`, en `<pad>.mp4` als `kaal` waar is.
 * Er wordt bewust niet met HTTP-requests naar bestanden gezocht (404-stormen gaven eerder
 * ip-blokkades); nieuwe video = mp4 in `public/resources/video/` plaatsen én hier de regel bijwerken.
 */
interface PickerKnop {
    pad: string;
    cssKlasse: string;
    titelKey: string;
    talen?: string[];
    kaal?: boolean;
    minNiveau?: number;
    tekst?: string;
}

/**
 * Eén rij knoppen in de picker, met optioneel een scheidingslijn erboven.
 */
interface PickerRij {
    scheiding?: boolean;
    knoppen: PickerKnop[];
}

/**
 * De pickerinhoud per plat modelleerniveau (2/3/4), overgenomen uit de drie oorspronkelijke
 * markupblokken in canvas.html. De level4-knoppen configuration/agent verwijzen bewust naar de
 * level3-video's: level4-varianten bestaan niet.
 */
const PICKER_CONFIG: Record<2 | 3 | 4, PickerRij[]> = {
    2: [
        {knoppen: [
            {pad: 'level2/examples', cssKlasse: 'fullvideo', titelKey: 'VIDEO.FULL', talen: ['en', 'nl']},
            {pad: 'level2/simulate', cssKlasse: 'simulate', titelKey: 'VIDEO.SIMULATE', talen: ['en', 'nl']}
        ]},
        {scheiding: true, knoppen: [
            {pad: 'level2/entity', cssKlasse: 'entity', titelKey: 'VIDEO.ENTITY', talen: ['en', 'nl']},
            {pad: 'level2/configuration', cssKlasse: 'configuration', titelKey: 'VIDEO.CONFIGURATION', talen: ['en', 'nl']}
        ]},
        {scheiding: true, knoppen: [
            {pad: 'level2/quantity', cssKlasse: 'quantity', titelKey: 'VIDEO.QUANTITY', talen: ['en', 'nl']},
            {pad: 'level2/derivative_inequality', cssKlasse: 'inequality', titelKey: 'VIDEO.INEQUALITY', talen: ['en', 'nl'], minNiveau: 25},
            {pad: 'level2/causality', cssKlasse: 'causal', titelKey: 'VIDEO.CAUSAL', talen: ['en', 'nl']}
        ]},
        {knoppen: [
            {pad: 'level2/value', cssKlasse: 'value', titelKey: 'VIDEO.VALUE', talen: ['en', 'nl']}
        ]}
    ],
    3: [
        {knoppen: [
            {pad: 'level3/examples', cssKlasse: 'fullvideo', titelKey: 'VIDEO.FULL', talen: ['en', 'nl']},
            {pad: 'level3/simulate', cssKlasse: 'simulate', titelKey: 'VIDEO.SIMULATE', talen: ['en', 'nl']}
        ]},
        {scheiding: true, knoppen: [
            {pad: 'level3/entity', cssKlasse: 'entity', titelKey: 'VIDEO.ENTITY', talen: ['en', 'nl']},
            {pad: 'level3/configuration', cssKlasse: 'configuration', titelKey: 'VIDEO.CONFIGURATION', talen: ['en', 'nl']},
            {pad: 'level3/agent', cssKlasse: 'agent', titelKey: 'VIDEO.AGENT', kaal: true}
        ]},
        {scheiding: true, knoppen: [
            {pad: 'level3/quantity', cssKlasse: 'quantity', titelKey: 'VIDEO.QUANTITY', talen: ['en', 'nl']},
            {pad: 'level3/causality', cssKlasse: 'causal', titelKey: 'VIDEO.CAUSAL', talen: ['en', 'nl']},
            {pad: 'level3/quantityspace', cssKlasse: 'quantity_space', titelKey: 'VIDEO.QUANTITYSPACE', talen: ['en', 'nl']}
        ]},
        {knoppen: [
            {pad: 'level3/value', cssKlasse: 'value', titelKey: 'VIDEO.VALUE', talen: ['en', 'nl']},
            {pad: 'level3/correspondence', cssKlasse: 'correspondence', titelKey: 'VIDEO.CORRESPONDENCE', talen: ['en', 'nl']},
            {pad: 'level3/exogenous', cssKlasse: 'exogenous', titelKey: 'VIDEO.EXOGENOUS', talen: ['en', 'nl']}
        ]},
        {scheiding: true, knoppen: [
            {pad: 'level3/valuehistory', cssKlasse: 'valuehistory', titelKey: 'VIDEO.VALUEHISTORY', talen: ['en', 'nl']}
        ]}
    ],
    4: [
        {knoppen: [
            {pad: 'level4/examples', cssKlasse: 'fullvideo', titelKey: 'VIDEO.FULL', talen: ['en', 'nl']},
            {pad: 'level4/simulate', cssKlasse: 'simulate', titelKey: 'VIDEO.SIMULATE', talen: ['en', 'nl']}
        ]},
        {scheiding: true, knoppen: [
            {pad: 'level4/entity', cssKlasse: 'entity', titelKey: 'VIDEO.ENTITY', talen: ['en', 'nl']},
            {pad: 'level3/configuration', cssKlasse: 'configuration', titelKey: 'VIDEO.CONFIGURATION', talen: ['en', 'nl']},
            {pad: 'level3/agent', cssKlasse: 'agent', titelKey: 'VIDEO.AGENT', kaal: true}
        ]},
        {knoppen: [
            {pad: 'level4/assumption', cssKlasse: 'assumption', titelKey: 'VIDEO.ASSUMPTION', talen: ['en', 'nl']}
        ]},
        {scheiding: true, knoppen: [
            {pad: 'level4/quantity', cssKlasse: 'quantity', titelKey: 'VIDEO.QUANTITY', talen: ['en', 'nl']},
            {pad: 'level4/causality', cssKlasse: 'causal', titelKey: 'VIDEO.CAUSAL', talen: ['en', 'nl']},
            {pad: 'level4/quantityspace', cssKlasse: 'quantity_space', titelKey: 'VIDEO.QUANTITYSPACE', talen: ['en', 'nl']}
        ]},
        {knoppen: [
            {pad: 'level4/value', cssKlasse: 'value', titelKey: 'VIDEO.VALUE', talen: ['en', 'nl']},
            {pad: 'level4/inequality', cssKlasse: 'inequality', titelKey: 'VIDEO.INEQUALITY', talen: ['en', 'nl']},
            {pad: 'level4/correspondence', cssKlasse: 'correspondence', titelKey: 'VIDEO.CORRESPONDENCE', talen: ['en', 'nl']}
        ]},
        {knoppen: [
            {pad: 'level4/exogenous', cssKlasse: 'exogenous', titelKey: 'VIDEO.EXOGENOUS', talen: ['en', 'nl']},
            {pad: 'level4/calculus', cssKlasse: 'calculus', titelKey: 'VIDEO.CALCULUS', talen: ['en', 'nl']}
        ]},
        {scheiding: true, knoppen: [
            {pad: 'level4/valuehistory', cssKlasse: 'valuehistory', titelKey: 'VIDEO.VALUEHISTORY', talen: ['en', 'nl']},
            {pad: 'level4/conditional', cssKlasse: 'conditional', titelKey: 'VIDEO.CONDITIONAL', talen: ['en', 'nl'], minNiveau: 50, tekst: 'CC'}
        ]}
    ]
};

@Component({
    selector: 'dl-video',
    standalone: true,
    templateUrl: './dl-video.component.html',
    styleUrls: ['./dl-video.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, TranslatePipe],
    host: {
        '[class.dl-video--zichtbaar]': 'zichtbaar()',
        '[class.dl-video--bovenste]': 'bovenste()'
    }
})
export class DlVideoComponent extends BaseIslandComponent implements AfterViewInit, OnDestroy {

    /**
     * De vertaalservice, voor de titels en de taalkeuze van de videobestanden.
     */
    private readonly translateService = inject(TranslateService);

    /**
     * Het modeldocument, voor het effectieve modelleerniveau.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De gedeelde videostatus: play-state, gating en de start- en stopverzoeken van buiten.
     */
    protected readonly videoStatus = inject(VideoStatusService);

    /**
     * De condtag-state: bij een actieve conditieset zijn de tekentools boven deze knop verborgen.
     */
    private readonly condtagService = inject(CondtagService);

    /**
     * Het overlay-element; wordt naar document.body verplaatst (zie het template-commentaar).
     */
    @ViewChild('overlay') private overlay?: ElementRef<HTMLDivElement>;

    constructor() {
        super();
        //een startverzoek van buiten (de opstart): speel de startvideo. Het eerste effect loopt
        //op de beginwaarde 0 en doet dan niets
        effect(() => {
            if (this.videoStatus.startverzoek() > 0) {
                untracked(() => this.initVideo());
            }
        });
        //de video is van buiten uitgezet: een open picker gaat ook dicht
        effect(() => {
            this.videoStatus.sluitverzoek();
            untracked(() => {
                this.pickerOpen.set(false);
                this.cdr.markForCheck();
            });
        });
    }

    // --- Interne state ---

    /** Open/dicht state van de picker. */
    protected readonly pickerOpen = signal(false);

    /** De bestandsnaam (binnen /resources/video) van de spelende video. */
    protected readonly videoNaam = signal<string | null>(null);

    /** De knop is alleen te zien als de video in dit project aanstaat. */
    protected readonly zichtbaar = computed(() => this.videoStatus.magVideo());

    /**
     * Of deze knop het bovenste item in de tekenbalk is: bij een actieve conditieset zijn de
     * tekentools erboven verborgen en horen de scheidingslijn en de marge weg.
     */
    protected readonly bovenste = computed(() => !!this.condtagService.condtag());

    /**
     * De pickerrijen voor het effectieve modelleerniveau van het model, met de knoppen die onder
     * dat niveau vallen eruit gefilterd. Leeg onder niveau 20.
     */
    protected readonly pickerRijen = computed<PickerRij[]>(() => {
        const effectief = this.dlModel.effectief_modelleerniveau;
        if (effectief < 20) {
            return [];
        }
        const config = PICKER_CONFIG[Math.min(Math.floor(effectief / 10), 4) as 2 | 3 | 4];
        return config
            .map(rij => ({...rij, knoppen: rij.knoppen.filter(knop => !knop.minNiveau || effectief >= knop.minNiveau)}))
            .filter(rij => rij.knoppen.length);
    });

    // --- Lifecycle hooks ---

    /**
     * Verplaatst de overlay naar document.body, buiten de stacking context van het sidemenu.
     * De Angular-bindingen blijven gewoon werken; alleen de plek in de DOM verandert.
     */
    ngAfterViewInit(): void {
        if (this.overlay) {
            document.body.appendChild(this.overlay.nativeElement);
        }
    }

    /**
     * Ruimt het naar document.body verplaatste overlay-element op.
     */
    ngOnDestroy(): void {
        this.overlay?.nativeElement.remove();
    }

    // --- Video starten en stoppen ---

    /**
     * Start de startvideo (de algemene voorbeelden) voor het huidige niveau. Op een startverzoek
     * van de `VideoStatusService` (de opstart) en bij een klik op de knop op een niveau zonder
     * picker.
     */
    protected initVideo(): void {
        if (!this.videoStatus.magVideo()) {
            this.videoStatus.zetSpeelt(false);
            this.cdr.markForCheck();
            return;
        }
        const plat = this.platNiveau();
        if (plat < 2) {
            this.videoStatus.zetSpeelt(false);
        } else {
            this.speel(this.vindKnop(`level${Math.min(plat, 4)}/examples`));
        }
        this.cdr.markForCheck();
    }

    /**
     * Zet een spelende video uit en sluit de picker, vanuit de overlay en de knop. De flows buiten
     * het eiland (opslaan, laden, de simulatie) roepen sinds golf 4c `VideoStatusService.cancel()`
     * aan.
     */
    protected cancelVideo(): void {
        this.videoStatus.cancel();
        this.pickerOpen.set(false);
        this.cdr.markForCheck();
    }

    // --- Interactie ---

    /**
     * De knop in de tekenbalk: sluit een open picker, cancelt een spelende video, en opent anders
     * de picker (of speelt direct bij een niveau zonder picker).
     */
    protected klikKnop(): void {
        if (this.pickerOpen()) {
            this.pickerOpen.set(false);
        } else if (this.videoStatus.speelt()) {
            this.cancelVideo();
        } else if (this.platNiveau() >= 2) {
            this.pickerOpen.set(true);
        } else {
            this.initVideo();
        }
    }

    /**
     * Een keuze in de picker: speel de gekozen video en sluit de picker.
     */
    protected pick(knop: PickerKnop): void {
        if (this.videoStatus.magVideo()) {
            this.speel(knop);
        }
    }

    /**
     * Een klik buiten het eiland sluit de picker. Bewust een target-check in plaats van
     * stopPropagation op de eigen kliks: de klik moet gewoon doorbubbelen, zodat bijvoorbeeld de
     * projectswitch-dropdown in de toolbar (eigen document-listener) meesluit bij een klik op de
     * videoknop.
     */
    @HostListener('document:click', ['$event'])
    protected documentKlik(gebeurtenis: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(gebeurtenis.target as Node)) {
            this.pickerOpen.set(false);
            this.cdr.markForCheck();
        }
    }

    // --- Private methoden ---

    /**
     * Speelt de video van de opgegeven knop: bestandsnaam bepalen via de statische lijst, de
     * play-state zetten en de actie loggen.
     */
    private speel(knop: PickerKnop | null): void {
        const naam = knop && this.bepaalBestandsnaam(knop);
        if (!naam) {
            this.log.warn(`Geen videobestand beschikbaar`, knop?.pad);
            return;
        }
        this.videoNaam.set(naam);
        this.videoStatus.zetSpeelt(true);
        this.videoStatus.directlog('play_video', {play: true, video: naam, picker: false});
        this.pickerOpen.set(false);
    }

    /**
     * Bepaalt de bestandsnaam voor een knop uit de statische lijst: de huidige taal, anders en,
     * anders nl, anders de kale naam; null als er niets beschikbaar is.
     */
    private bepaalBestandsnaam(knop: PickerKnop): string | null {
        for (const taal of [this.translateService.currentLang, 'en', 'nl']) {
            if (knop.talen?.includes(taal)) {
                return `${knop.pad}_${taal}.mp4`;
            }
        }
        return knop.kaal ? `${knop.pad}.mp4` : null;
    }

    /**
     * Het platte modelleerniveau (2/3/4/5): uit het effectieve niveau van het model, anders uit
     * het projectniveau, anders 2 (de logica van ng1's `platModelleerNiveau`).
     */
    private platNiveau(): number {
        const niveau = this.dlModel.effectief_modelleerniveau || this.videoStatus.projectniveau();
        return niveau ? Math.floor(niveau / 10) : 2;
    }

    /**
     * Zoekt de pickerknop met het opgegeven videopad in de volledige configuratie.
     */
    private vindKnop(pad: string): PickerKnop | null {
        for (const rijen of Object.values(PICKER_CONFIG)) {
            for (const rij of rijen) {
                const knop = rij.knoppen.find(k => k.pad === pad);
                if (knop) {
                    return knop;
                }
            }
        }
        return null;
    }
}
